/* Hameldown navigation v6. No network requests, tracking, or framework dependencies. */
(() => {
  'use strict';
  if (customElements.get('hameldown-nav')) return;
  const FOCUSABLE = 'a[href],button:not([disabled]),summary,input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex="0"]';
  class HameldownNav extends HTMLElement {
    connectedCallback() {
      if (this._controller) return;
      this._controller = new AbortController();
      this._inert = new Map();
      this._hover = matchMedia('(hover: hover) and (pointer: fine)');
      const signal = this._controller.signal;
      this.addEventListener('click', e => this._click(e), { signal });
      this.addEventListener('keydown', e => this._key(e), { signal });
      this.addEventListener('pointerover', e => this._enter(e), { signal });
      this.addEventListener('pointerout', e => this._leave(e), { signal });
      this.addEventListener('focusout', () => {
        clearTimeout(this._focusTimer);
        this._focusTimer = setTimeout(() => { if (!this.contains(document.activeElement) && this.dataset.mode === 'desktop') this.close(); }, 0);
      }, { signal });
      // Capturing toggle also catches native details changes and no forced keyboard focus on hover.
      this.addEventListener('toggle', e => {
        const details = e.target;
        if (!(details instanceof HTMLDetailsElement)) return;
        const summary = details.querySelector(':scope > summary');
        summary?.setAttribute('aria-expanded', String(details.open));
        if (details.matches('[data-hdn-mobile]')) details.open ? this._lock() : this._unlock();
      }, { capture: true, signal });
      document.addEventListener('pointerdown', e => {
        if (!e.composedPath().includes(this)) this.close(false);
      }, { signal, passive: true });
      window.addEventListener('resize', () => {
        if (!this.getClientRects().length || getComputedStyle(this).display === 'none') this.close(false);
        else this._measure();
      }, { signal, passive: true });
      window.addEventListener('scroll', () => this._queueMeasure(), { signal, passive: true });
      window.addEventListener('pageshow', e => { if (e.persisted) this.close(false); }, { signal });
      this.addEventListener('error', e => {
        if (e.target instanceof HTMLImageElement && e.target.classList.contains('hdn-brand-photo')) e.target.hidden = true;
      }, { capture: true, signal });
      this.dataset.enhanced = 'true';
      this._refresh();
      // Theme section hydration can morph descendants without reconnecting this element.
      this._observer = new MutationObserver(() => {
        if (this._mobileOpen && !this.querySelector('[data-hdn-mobile][open]')) this._unlock();
        this._refresh();
      });
      this._observer.observe(this, { childList: true, subtree: true });
      if (this.querySelector('[data-hdn-mobile][open]')) this._lock();
    }
    disconnectedCallback() {
      this._clearTimers(); clearTimeout(this._focusTimer); cancelAnimationFrame(this._raf);
      this._observer?.disconnect(); this._controller?.abort(); this._controller = null;
      this._unlock();
    }
    _refresh() {
      this.querySelectorAll('details').forEach(d => d.querySelector(':scope > summary')?.setAttribute('aria-expanded', String(d.open)));
      this.querySelectorAll('.hdn-brand-photo').forEach(img => { if (img.complete && img.naturalWidth === 0) img.hidden = true; });
      if (this.dataset.mode === 'desktop' && this.querySelector('[data-hdn-dropdown][open]')) this._measure();
    }
    _clearTimers() { clearTimeout(this._openTimer); clearTimeout(this._closeTimer); }
    _click(event) {
      if (!(event.target instanceof Element)) return;
      const mobile = this.querySelector('[data-hdn-mobile]');
      const trigger = event.target.closest('summary');
      if (trigger?.parentElement?.matches('[data-hdn-dropdown]')) {
        event.preventDefault();
        const details = trigger.parentElement;
        // First click on a hover-open panel pins it; a second click closes it.
        if (details.open && this._pinned === details) this.close();
        else this._open(details, true);
      } else if (trigger?.parentElement === mobile) {
        event.preventDefault();
        if (mobile.open) this.close(true); else { mobile.open = true; this._lock(); }
      } else if (event.target.closest('[data-hdn-close]')) {
        this.close(true);
      } else if (event.target.closest('[data-hdn-mobile-close],[data-hdn-backdrop]')) {
        this.close(true);
      } else if (event.target.closest('a[href]')) {
        // Preserve default navigation including modified clicks and preview-theme parameters.
        this.close(false);
      }
    }
    _open(details, pinned = false) {
      this._clearTimers();
      this.querySelectorAll('[data-hdn-dropdown][open]').forEach(other => { if (other !== details) other.open = false; });
      details.open = true;
      if (pinned) this._pinned = details;
      else if (this._pinned !== details) this._pinned = null;
      details.querySelector('summary')?.setAttribute('aria-expanded', 'true');
      this._measure();
    }
    close(restoreFocus = false) {
      this._clearTimers();
      const active = this.querySelector('[data-hdn-dropdown][open],[data-hdn-mobile][open]');
      if (!active) { this._pinned = null; this._unlock(); return; }
      const summary = active.querySelector(':scope > summary');
      active.open = false; summary?.setAttribute('aria-expanded', 'false');
      this._pinned = null; this._unlock();
      if (restoreFocus && summary?.isConnected) summary.focus({ preventScroll: true });
    }
    _enter(event) {
      if (this.dataset.mode !== 'desktop' || !this._hover.matches || event.pointerType === 'touch' || !(event.target instanceof Element)) return;
      const details = event.target.closest('[data-hdn-dropdown]');
      if (!details || !this.contains(details)) return;
      if (event.relatedTarget instanceof Node && details.contains(event.relatedTarget)) return;
      this._clearTimers();
      if (!details.open) {
        const configured = Number(this.dataset.openDelay ?? 150);
        const delay = Number.isFinite(configured) ? Math.max(0, Math.min(400, configured)) : 150;
        this._openTimer = setTimeout(() => this._open(details), delay);
      }
    }
    _leave(event) {
      if (this.dataset.mode !== 'desktop' || !this._hover.matches || event.pointerType === 'touch' || !(event.target instanceof Element)) return;
      const details = event.target.closest('[data-hdn-dropdown]');
      if (!details) return;
      if (event.relatedTarget instanceof Node && details.contains(event.relatedTarget)) return;
      clearTimeout(this._openTimer); clearTimeout(this._closeTimer);
      if (this._pinned === details || details.contains(document.activeElement)) return;
      this._closeTimer = setTimeout(() => { if (!details.matches(':hover') && !details.contains(document.activeElement)) this.close(); }, Number(this.dataset.closeDelay) || 450);
    }
    _key(event) {
      if (event.key === 'Escape') {
        if (this.querySelector('[data-hdn-dropdown][open],[data-hdn-mobile][open]')) { event.preventDefault(); event.stopPropagation(); this.close(true); }
        return;
      }
      const summary = event.target instanceof Element ? event.target.closest('.hdn-trigger') : null;
      if (summary && ['ArrowDown','ArrowUp'].includes(event.key)) {
        event.preventDefault(); const details = summary.parentElement; this._open(details, true);
        const links = [...details.querySelectorAll('.hdn-panel a[href],.hdn-panel button')].filter(el => el.getClientRects().length);
        (event.key === 'ArrowUp' ? links.at(-1) : links[0])?.focus();
      }
      if (event.key === 'Tab' && this._mobileOpen) {
        const panel = this.querySelector('.hdn-mobile-panel');
        const items = [...panel.querySelectorAll(FOCUSABLE)].filter(el => el.getClientRects().length && getComputedStyle(el).visibility !== 'hidden');
        const first = items[0], last = items.at(-1);
        if (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }
    }
    _measure() {
      const row = this.closest('.header__row');
      if (!row || this.dataset.mode !== 'desktop') return;
      this.style.setProperty('--hdn-panel-top', `${Math.max(0, Math.round(row.getBoundingClientRect().bottom))}px`);
    }
    _queueMeasure() {
      if (this._raf || !this.querySelector('[data-hdn-dropdown][open]')) return;
      this._raf = requestAnimationFrame(() => { this._raf = null; this._measure(); });
    }
    _lock() {
      if (this._mobileOpen || this.dataset.mode !== 'mobile') return;
      this._mobileOpen = true;
      const panel = this.querySelector('.hdn-mobile-panel');
      panel?.setAttribute('role','dialog'); panel?.setAttribute('aria-modal','true');
      this._ownedScrollLock = !document.documentElement.classList.contains('hdn-scroll-lock');
      document.documentElement.classList.add('hdn-scroll-lock');
      // Make content outside the active drawer inert, remembering earlier values.
      let branch = this;
      while (branch.parentElement && branch !== document.body) {
        for (const sibling of branch.parentElement.children) {
          if (sibling === branch || !(sibling instanceof HTMLElement) || /^(SCRIPT|STYLE|LINK)$/.test(sibling.tagName)) continue;
          if (!this._inert.has(sibling)) this._inert.set(sibling, sibling.inert);
          sibling.inert = true;
        }
        branch = branch.parentElement;
      }
      this.querySelector('.hdn-mobile-trigger')?.setAttribute('aria-expanded','true');
      this._focusRaf = requestAnimationFrame(() => { if (this._mobileOpen) this.querySelector('[data-hdn-mobile-close]')?.focus({ preventScroll:true }); });
    }
    _unlock() {
      cancelAnimationFrame(this._focusRaf);
      if (!this._mobileOpen) return;
      this._mobileOpen = false;
      this._inert?.forEach((value, el) => { el.inert = value; }); this._inert?.clear();
      if (this._ownedScrollLock) document.documentElement.classList.remove('hdn-scroll-lock');
      this._ownedScrollLock = false;
      const panel = this.querySelector('.hdn-mobile-panel'); panel?.removeAttribute('role'); panel?.removeAttribute('aria-modal');
    }
  }
  customElements.define('hameldown-nav', HameldownNav);
})();
